import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const TrustFrameworkPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>({
        email: '',
        type: 'user',
        ageConfirm: false
      });
    const [isSubmitting, setIsSubmitting] = useState(false);
      const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Trust Framework — Auditable Loyalty Standards | Rewardly';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Six core principles embedded in every smart contract. Transparent, auditable, non-extractive by design. Pre-deployment validation checklist.');
    setMeta('og:title', 'Trust Framework — Rewardly Protocol Development Standards');
    setMeta('og:description', 'Every contract follows six core principles. Trust embedded in code — not promises. Patent pending.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/trust-framework');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'Trust Framework — Rewardly Protocol Development Standards');
    setMeta('twitter:description', 'Every contract follows six core principles. Trust embedded in code — not promises. Patent pending.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/trust-framework');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };
      const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        
        // Validate email
        if (!validateEmail(formData.email)) {
          setSubmitMessage('Please enter a valid email address');
          return;
        }
    
        // Validate age confirmation
        if (!formData.ageConfirm) {
          setSubmitMessage('Please confirm you are 18 or older');
          return;
        }
        setIsSubmitting(true);
        setSubmitMessage('');
        try {
          const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              email: formData.email,
              user_type: formData.type
            })
          });
    
          const raw = await response.json();
          let data: any;
          if (raw.body && typeof raw.body === 'string') {
            try { data = JSON.parse(raw.body); } catch { data = raw; }
          } else { data = raw; }
          const statusCode = raw.statusCode || response.status;
          if (statusCode === 200 && !data.error) {
            if (data.new_subscription) {
              setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
            } else {
              setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
            }
            setFormData({ email: '', type: 'user', ageConfirm: false });
            setTimeout(() => {
              setModalOpen(false);
              setSubmitMessage('');
            }, 3000);
          } else {
            setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
          }
        } catch (error) {
          console.error('Error submitting email:', error);
          setSubmitMessage('An error occurred. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error message when user starts typing
        if (submitMessage) setSubmitMessage('');
      };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Fira+Code:wght@400;500&family=Outfit:wght@400;500;600;700;800&display=swap');
        
        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
          --code-bg: #1E293B;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.7;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .container-narrow {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 24px;
        }

        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover { color: var(--blue-600); }

        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        .dropdown.nested {
          position: relative;
        }

        .dropdown.nested > a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          cursor: pointer;
        }

        .dropdown.nested > a::after {
          content: '→';
          font-size: 12px;
          margin-left: auto;
          transition: transform 0.2s;
        }

        .dropdown.nested:hover > a::after {
          transform: translateX(2px);
        }

        .dropdown > .dropdown-menu > .dropdown.nested > .dropdown-menu {
          position: absolute;
          top: 0;
          left: 100%;
          margin-top: 0;
          margin-left: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateX(-10px);
          transition: all 0.2s;
          pointer-events: none;
        }

        .dropdown > .dropdown-menu > .dropdown.nested:hover > .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateX(0);
          pointer-events: auto;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary { background: var(--blue-600); color: var(--white); }
        .btn-primary:hover { background: var(--blue-700); }
        .btn-outline { background: transparent; color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-outline:hover { border-color: var(--blue-600); color: var(--blue-600); }
        .btn-gold { background: var(--gold); color: var(--gray-900); }
        .btn-gold:hover { background: var(--gold-dark); }
        .btn-secondary { background: var(--white); color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-secondary:hover { background: var(--gray-100); }

        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span { width: 22px; height: 2px; background: var(--gray-900); }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          z-index: 999;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        .modal-overlay {
          display: none;
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }
        .modal-overlay.active { display: flex; }
        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }
        .modal h3 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
        .modal-field { margin-bottom: 20px; }
        .modal-field label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-500); margin-bottom: 8px; }
        .modal-field input, .modal-field select {
          width: 100%; padding: 14px 16px; border: 1px solid var(--gray-200);
          border-radius: 8px; font-size: 15px; font-family: inherit;
        }
        .modal-field input:focus, .modal-field select:focus { outline: none; border-color: var(--blue-600); }
        .modal-checkbox { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 24px; }
        .modal-checkbox input { width: 18px; height: 18px; margin-top: 2px; accent-color: var(--blue-600); }
        .modal-checkbox span { font-size: 14px; color: var(--gray-500); line-height: 1.5; }
        .modal-buttons { display: flex; gap: 12px; }
        .modal-buttons .btn { flex: 1; justify-content: center; padding: 14px 20px; }

        .hero {
          padding: 140px 0 60px;
          background: var(--off-white);
          border-bottom: 1px solid var(--gray-200);
          text-align: center;
        }

        .hero-label {
          display: inline-block;
          background: var(--white);
          border: 1px solid var(--gray-200);
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 600;
          color: var(--gray-500);
          margin-bottom: 24px;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .hero-subtitle {
          font-size: 17px;
          color: var(--gray-500);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
          letter-spacing: 0.3px;
        }

        .toc-section {
          padding: 48px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .toc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .toc-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: var(--off-white);
          border-radius: 8px;
          text-decoration: none;
          color: var(--gray-900);
          font-weight: 500;
          font-size: 14px;
          transition: all 0.2s;
        }

        .toc-item:hover {
          background: var(--blue-600);
          color: var(--white);
        }

        .toc-number {
          font-weight: 700;
          color: var(--gold);
        }

        .toc-item:hover .toc-number { color: var(--white); }

        .content-section {
          padding: 64px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .content-section:last-of-type { border-bottom: none; }

        .section-number {
          display: inline-block;
          background: var(--blue-600);
          color: var(--white);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          margin-bottom: 12px;
        }

        .section-intro {
          font-size: 17px;
          color: var(--gray-500);
          margin-bottom: 32px;
          max-width: 640px;
        }

        .principles-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .principle-card {
          background: var(--off-white);
          padding: 24px;
          border-radius: 12px;
          border-left: 3px solid var(--green-600);
        }

        .principle-card h4 {
          font-size: 16px;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 8px;
        }

        .principle-card p {
          color: var(--gray-500);
          font-size: 14px;
          line-height: 1.6;
        }

        .checklist-section {
          background: var(--gray-900);
          color: var(--white);
          padding: 64px 0;
        }

        .checklist-section .section-number { background: rgba(212, 168, 83, 0.15); color: var(--gold); }
        .checklist-section .section-title { color: var(--white); }
        .checklist-section .section-intro { color: rgba(255,255,255,0.7); }

        .checklist-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .checklist-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 28px;
          border-radius: 12px;
        }

        .checklist-card h3 {
          font-size: 16px;
          font-weight: 700;
          color: var(--gold);
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }

        .checklist-items {
          list-style: none;
        }

        .checklist-items li {
          padding: 6px 0;
          color: rgba(255,255,255,0.8);
          font-size: 14px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .check-icon {
          width: 18px;
          height: 18px;
          background: var(--green-600);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .check-icon svg {
          width: 10px;
          height: 10px;
          stroke: white;
          stroke-width: 3;
        }

        .code-block {
          background: var(--code-bg);
          border-radius: 12px;
          padding: 24px;
          overflow-x: auto;
          margin: 24px 0;
        }

        .code-block pre {
          font-family: 'Fira Code', monospace;
          font-size: 13px;
          color: #E2E8F0;
          line-height: 1.6;
          margin: 0;
          min-width: 0;
          overflow-x: auto;
        }

        .code-block .comment { color: #64748B; }
        .code-block .keyword { color: #C084FC; }
        .code-block .string { color: #4ADE80; }

        .template-card {
          background: var(--off-white);
          border-radius: 12px;
          padding: 28px;
          margin: 24px 0;
          border: 1px solid var(--gray-200);
        }

        .template-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 20px;
        }

        .template-row {
          display: flex;
          padding: 12px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .template-row:last-child { border-bottom: none; }

        .template-label {
          width: 160px;
          font-weight: 600;
          color: var(--gray-900);
          font-size: 14px;
        }

        .template-value {
          color: var(--gray-500);
          font-family: 'Fira Code', monospace;
          font-size: 13px;
        }

        .status-approved {
          display: inline-block;
          background: var(--green-600);
          color: var(--white);
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
        }

        .enforcement-box {
          background: linear-gradient(135deg, #1E3A5F 0%, #2d4a6f 100%);
          border-radius: 12px;
          padding: 32px;
          margin: 24px 0;
          color: var(--white);
        }

        .enforcement-box h3 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .enforcement-box p {
          color: rgba(255,255,255,0.8);
          font-size: 15px;
          line-height: 1.7;
        }

        .issued-section {
          padding: 48px 0;
          background: var(--off-white);
          text-align: center;
          border-top: 1px solid var(--gray-200);
        }

        .issued-label {
          font-size: 14px;
          color: var(--gray-500);
          margin-bottom: 8px;
        }

        .issued-org {
          font-size: 24px;
          font-weight: 800;
          color: var(--gray-900);
        }

        .links-section {
          padding: 64px 0;
          text-align: center;
        }

        .links-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 24px;
        }

        .links-grid {
          display: flex;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .link-card {
          padding: 16px 28px;
          background: var(--off-white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          text-decoration: none;
          color: var(--gray-900);
          font-weight: 600;
          font-size: 14px;
          transition: all 0.2s;
        }

        .link-card:hover {
          background: var(--blue-600);
          color: var(--white);
          border-color: var(--blue-600);
        }

        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          text-align: center;
        }

        .cta-section h2 {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 12px;
        }

        .cta-section p {
          color: rgba(255,255,255,0.7);
          font-size: 17px;
          margin-bottom: 32px;
        }

        footer {
          background: var(--gray-900);
          color: var(--white);
          padding: 60px 0 40px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-brand h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .footer-brand p {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .footer-links h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .footer-grid a {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }

        .footer-grid a:hover {
          color: var(--white);
        }

        .footer-social h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.6);
          transition: all 0.2s;
        }

        .social-links a:hover {
          background: rgba(255,255,255,0.2);
          color: var(--white);
        }

        .footer-bottom {
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }

          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }
          .hero-subtitle { font-size: 15px; }

          .toc-grid { grid-template-columns: 1fr; }
          .principles-grid, .checklist-grid { grid-template-columns: 1fr; }
          .template-row { flex-direction: column; gap: 4px; }
          .template-label { width: 100%; }
          .links-grid { flex-direction: column; align-items: center; }
          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }
          .modal-buttons { flex-direction: column; }
          .code-block { padding: 16px; }
          .code-block pre { font-size: 10px; white-space: pre; overflow-x: auto; }
        }
      `}</style>

        {/* Modal */}
     <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

     <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-label">Development Standards · Patent Pending</div>
          <h1>Trust Framework</h1>
          <p className="hero-subtitle">Every contract follows six core principles. Transparent, auditable, non-extractive by design. Trust embedded directly in code — not promises. Earn-time commitment architecture patent pending.</p>
        </div>
      </section>

      {/* TOC */}
      <section className="toc-section">
        <div className="container-narrow">
          <div className="toc-grid">
            <a href="#purpose" className="toc-item"><span className="toc-number">01</span> Purpose</a>
            <a href="#principles" className="toc-item"><span className="toc-number">02</span> Core Principles</a>
            <a href="#checklist" className="toc-item"><span className="toc-number">03</span> Validation Checklist</a>
            <a href="#clause" className="toc-item"><span className="toc-number">04</span> Trust Clause</a>
            <a href="#enforcement" className="toc-item"><span className="toc-number">05</span> Enforcement</a>
          </div>
        </div>
      </section>

      {/* Purpose */}
      <section className="content-section" id="purpose">
        <div className="container-narrow">
          <div className="section-number">Section 01</div>
          <h2 className="section-title">Purpose of the Framework</h2>
          <p className="section-intro">This framework ensures that every deployed smart contract upholds Rewardly Protocol's commitment to transparency, immutability, fairness, and human-centric design.</p>
          <p style={{color: 'var(--gray-500)', lineHeight: '1.8'}}>
            It acts as a pre-deployment gate for developers and auditors to assess contract readiness 
            through the lens of trust and ethical architecture. All on-chain components must align with 
            the mission of restoring integrity in human value exchange.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="content-section" id="principles">
        <div className="container-narrow">
          <div className="section-number">Section 02</div>
          <h2 className="section-title">Core Principles</h2>
          <p className="section-intro">The Architecture of Trust defines six foundational principles that govern all Rewardly Protocol smart contracts.</p>

          <div className="principles-grid">
            <div className="principle-card">
              <h4>Transparency</h4>
              <p>All state-changing operations emit indexed events for complete auditability. Source code published with audit verification.</p>
            </div>
            <div className="principle-card">
              <h4>Regional Equity</h4>
              <p>Fair allocation and multiplier system across all geographic regions. No region locked out or discriminated against.</p>
            </div>
            <div className="principle-card">
              <h4>Karmic Simplicity</h4>
              <p>Does exactly what is promised. No hidden logic, no excessive complexity, no backdoors.</p>
            </div>
            <div className="principle-card">
              <h4>Human-Centric Security</h4>
              <p>Multi-layered access control, time-delayed critical operations, and emergency safeguards protect user interests.</p>
            </div>
            <div className="principle-card">
              <h4>Anti-Greed Tokenomics</h4>
              <p>Capped supply with hard limits. No hidden minting logic or centralized control that enables rug-pulls.</p>
            </div>
            <div className="principle-card">
              <h4>Code as Covenant</h4>
              <p>Immutable core logic with modular upgradeability only where necessary, governed by DAO-locked admin privileges.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="checklist-section" id="checklist">
        <div className="container-narrow">
          <div className="section-number">Section 03</div>
          <h2 className="section-title">Trust Validation Checklist</h2>
          <p className="section-intro">Pre-deployment requirements that every contract must satisfy before mainnet publication.</p>

          <div className="checklist-grid">
            <div className="checklist-card">
              <h3>Access Control & Ownership</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Use OpenZeppelin AccessControl</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Avoid onlyOwner; prefer onlyRole</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Renounce or DAO-lock admin privileges</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Document all role capabilities</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Supply Control</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Enforce capped total supply with hard limits</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>No hidden minting in fallback functions</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>All supply-altering functions emit events</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Burn logic is transparent and auditable</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Staking & Yield Logic</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Display all reward parameters externally</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Oracle-adjusted or fixed-rate logic audited</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Implement slashing guardrails</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Withdrawal delays documented</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Auditability</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Emit indexed events on sensitive actions</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Publish source code on-chain</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Link audit hash on-chain</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Complete NatSpec documentation</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Oracle & Dependencies</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Use Chainlink or equivalent</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Fallback behavior clearly defined</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Staleness checks implemented</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Circuit breakers for extreme values</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Upgradeability</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Apply UUPS or Transparent Proxy</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Governance-controlled admin</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Avoid personal wallets as proxy admin</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Timelock on upgrades</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Governance & DAO</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Support proposal/voting/execute</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Thresholds externally accessible</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Quorum requirements documented</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Execution delays enforced</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Security Standards</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Reentrancy guards on external calls</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Integer overflow protection</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Access control on all state changes</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Emergency pause functionality</li>
              </ul>
            </div>
            <div className="checklist-card">
              <h3>Pool Health Gating</h3>
              <ul className="checklist-items">
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Health factor &lt; 1.0 blocks all conversions</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Health factor &lt; 1.2 blocks direct claims</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>48h timelock on router changes</li>
                <li><span className="check-icon"><svg fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg></span>Configurable per-coalition daily conversion caps with 100M global safety limit</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Clause */}
      <section className="content-section" id="clause">
        <div className="container-narrow">
          <div className="section-number">Section 04</div>
          <h2 className="section-title">Trust Alignment Clause</h2>
          <p className="section-intro">Every Rewardly smart contract begins with this Trust Framework declaration — a binding commitment to transparent, ethical design embedded directly in the code.</p>

          <div className="code-block">
            <pre>{`/**
 * ============================================================================
 * REWARDLY PROTOCOL TRUST FRAMEWORK - DHARMA ALIGNMENT
 * ============================================================================
 *
 * This contract is part of the Rewardly Protocol ecosystem and adheres to the
 * Trust Framework principles established for restoring integrity in
 * human value exchange.
 *
 * CORE PRINCIPLES:
 *
 * • TRANSPARENCY: All state-changing operations emit indexed events for
 *   complete auditability. Source code published with audit verification.
 *
 * • REGIONAL EQUITY: Fair allocation and multiplier system across all
 *   geographic regions. No region locked out or discriminated against.
 *
 * • KARMIC SIMPLICITY: Does exactly what is promised. No hidden logic,
 *   no excessive complexity, no backdoors.
 *
 * • HUMAN-CENTRIC SECURITY: Multi-layered access control, time-delayed
 *   critical operations, and emergency safeguards protect user interests.
 *
 * • ANTI-GREED TOKENOMICS: Capped supply with hard limits. No hidden
 *   minting logic or centralized control that enables rug-pulls.
 *
 * • CODE AS COVENANT: Immutable core logic with modular upgradeability
 *   only where necessary, governed by DAO-locked admin privileges.
 *
 * This contract upholds the mission: "Restoring human trust in value exchange
 * through transparent, ethical, and immutable blockchain architecture."
 *
 * ============================================================================
 */`}</pre>
          </div>
        </div>
      </section>


      {/* Enforcement */}
      <section className="content-section" id="enforcement">
        <div className="container-narrow">
          <div className="section-number">Section 05</div>
          <h2 className="section-title">Enforcement</h2>

          <div className="enforcement-box">
            <h3>Zero Tolerance Policy</h3>
            <p>
              Contracts that bypass this framework or introduce undeclared supply manipulation, 
              hardcoded personal wallets, or backdoors must be blacklisted and flagged via on-chain 
              governance and community tools. There are no exceptions.
            </p>
          </div>

          <div className="principles-grid" style={{marginTop: '24px'}}>
            <div className="principle-card">
              <h4>Blacklist Triggers</h4>
              <p>Undeclared minting, hidden admin functions, personal wallet privileges, bypass of governance.</p>
            </div>
            <div className="principle-card">
              <h4>Community Reporting</h4>
              <p>Any community member can flag non-compliant contracts through governance proposals.</p>
            </div>
            <div className="principle-card">
              <h4>Automatic Detection</h4>
              <p>On-chain monitoring for unauthorized supply changes and role modifications.</p>
            </div>
            <div className="principle-card">
              <h4>Resolution Process</h4>
              <p>DAO vote required for contract rehabilitation. Full audit and remediation mandatory.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Issued By */}
      <section className="issued-section">
        <div className="container">
          <p className="issued-label">Issued by</p>
          <p className="issued-org">Rewardly Protocol Team</p>
          <p style={{fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '8px'}}>Patent Pending: PCT/US26/20246 · Rewardly Protocol, Inc.</p>
        </div>
      </section>

      {/* Links */}
      <section className="links-section">
        <div className="container">
          <h3 className="links-title">Related Documentation</h3>
          <div className="links-grid">
            <a href="/vision" className="link-card">Our Vision</a>
            <a href="/whitepaper" className="link-card">Technical Whitepaper</a>
            <a href="/manifesto" className="link-card">Silent Manifesto</a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Build With Trust</h2>
          <p>Join us in creating loyalty systems that honor human dignity.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Build With Trust →</button>
            <a href="/whitepaper" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Read the Whitepaper →</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TrustFrameworkPage;